import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'

import { Alert } from '@ui/Alert/Alert'
import { Button } from '@ui/components/Button'
import { DownloadIcon } from '@ui/icons/Download.icon'
import { IDIcon } from '@ui/icons/ID.icon'

import { isDepositBeforeKYC } from '@utils/AccountAccess/accountKYCStatuses'
import { useWindowResize } from '@utils/domUtils'
import { isSignUpPath } from '@utils/path'

import { AccountDetailedStatus } from '@model/AccountDetailedStatus'

import { useDocumentVerificationPath } from '@hooks/useDocumentVerificationPath'
import { useLocallyPersistedState } from '@hooks/useStorage'

import styles from './StatusBanner.module.scss'

const useBannerVisibility = () => {
  const location = useLocation()

  const [bannerOpen, setBannerOpen] = useLocallyPersistedState('statusBannerOpen', true)
  const { account } = useAccountReadContext()

  const isSignUpRoute = isSignUpPath(location.pathname)
  const isRedirectPath = location.pathname.includes('redirect')
  const isEligibleAccountStatus =
    (account &&
      [
        AccountDetailedStatus.AppropriatenessTestSubmitted,
        AccountDetailedStatus.PendingDocumentVerification,
      ].includes(account.status.id)) ??
    false

  const isBannerVisible = useMemo(
    () =>
      bannerOpen &&
      !isSignUpRoute &&
      !isRedirectPath &&
      isEligibleAccountStatus &&
      isDepositBeforeKYC(account),
    [bannerOpen, isSignUpRoute, isRedirectPath, isEligibleAccountStatus, account]
  )

  return { isBannerVisible, setBannerOpen }
}

export const StatusBanner: React.FC = () => {
  const isMobile = useWindowResize()
  const navigate = useNavigate()
  const { isBannerVisible, setBannerOpen } = useBannerVisibility()
  const docVerificationPath = useDocumentVerificationPath()

  const { t } = useTranslation()

  const handleNavigate = () => {
    navigate(docVerificationPath)
  }

  const handleClose = () => {
    setBannerOpen(false)
  }

  if (!isBannerVisible) {
    return null
  }

  return (
    <div className={styles.statusBanner}>
      <StatusBannerUpload
        text={
          isMobile
            ? t('Verification required')
            : t('To gain full access to your Client Area, please upload your documents')
        }
        onClick={handleNavigate}
        onClose={handleClose}
      />
    </div>
  )
}

interface WarningNoteProps {
  text: string
  onClick(): void
  onClose(): void
}

const StatusBannerUpload: React.FC<WarningNoteProps> = (props) => {
  const { text, onClick, onClose } = props

  const { t } = useTranslation()

  return (
    <Alert
      text={text}
      renderLeft={<IDIcon color='text-warning-high-em' />}
      renderRight={
        <Button.Primary size='sm' iconRight={DownloadIcon.Down} onClick={onClick}>
          {t('Profile.Upload')}
        </Button.Primary>
      }
      onClose={onClose}
    />
  )
}
